
import React from 'react';
import { User, MapPin } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Doctor, Staff, Room } from './SchedulerTypes';

interface SchedulePersonSelectionProps {
  selectedDoctor: string | undefined;
  setSelectedDoctor: (value: string | undefined) => void;
  doctors: Doctor[];
  selectedStaff: string | undefined;
  setSelectedStaff: (value: string | undefined) => void;
  staff: Staff[];
  orderedStaff?: Staff[]; // Optional ordered staff list based on doctor preferences
  selectedRoom: string | undefined;
  setSelectedRoom: (value: string | undefined) => void;
  rooms: Room[];
}

const SchedulePersonSelection: React.FC<SchedulePersonSelectionProps> = ({
  selectedDoctor,
  setSelectedDoctor,
  doctors,
  selectedStaff,
  setSelectedStaff,
  staff,
  orderedStaff,
  selectedRoom,
  setSelectedRoom,
  rooms,
}) => {
  // Use ordered staff if provided, otherwise use regular staff
  const staffToDisplay = orderedStaff || staff;
  return (
    <div className="space-y-4">
      <div className="text-sm text-muted-foreground">
        Select at least one person. You can assign both a doctor and staff member for collaborative appointments.
      </div>
      <div className="grid grid-cols-3 gap-4">
      <div>
        <Label htmlFor="doctor" className="text-sm font-medium">
          <User className="inline-block h-4 w-4 mr-2" />
          Doctor
        </Label>
        <Select value={selectedDoctor || 'none'} onValueChange={(value) => setSelectedDoctor(value === 'none' ? undefined : value)}>
          <SelectTrigger>
            <SelectValue placeholder="Select a doctor" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">None</SelectItem>
            {doctors.filter(doctor => doctor.status === 'active').map((doctor) => (
              <SelectItem key={doctor.id} value={doctor.id}>{doctor.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="staff" className="text-sm font-medium">
          <User className="inline-block h-4 w-4 mr-2" />
          Staff
        </Label>
        <Select value={selectedStaff || 'none'} onValueChange={(value) => setSelectedStaff(value === 'none' ? undefined : value)}>
          <SelectTrigger>
            <SelectValue placeholder="Select staff" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">None</SelectItem>
            {staffToDisplay.filter(s => s.status === 'active').map((s) => (
              <SelectItem key={s.id} value={s.id}>{s.short_name || s.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="room" className="text-sm font-medium">
          <MapPin className="inline-block h-4 w-4 mr-2" />
          Room
        </Label>
        <Select value={selectedRoom || 'none'} onValueChange={(value) => setSelectedRoom(value === 'none' ? undefined : value)}>
          <SelectTrigger>
            <SelectValue placeholder="Select a room" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">None</SelectItem>
            {rooms.filter(room => room.status === 'active').map((room) => (
              <SelectItem key={room.id} value={room.id}>{room.name} ({room.clinic})</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
    </div>
  );
};

export default SchedulePersonSelection;
